// React
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// UI Components
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { Button } from "ui/components/Button/Button";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";

// Services & Helpers
import { getVehicleInsuranceAPI } from "services/api/calculations/vehicleinsurance/api";
import { transformRoute, useTranslate } from "helpers";
import { Routes } from "ui/modules/calculation/navigation";

// Types & Constants
import { VehicleInsuranceDetailFormValues } from "services/api/calculations/vehicleinsurance/types";
import { Errors } from "services/forms/validation/types";
import {
  VehicleInsuranceDetailPageCmp,
  VehicleInsuranceDetailPageProps,
} from "./types";
import { initialVehicleInsuranceDetailForm } from "services/api/calculations/vehicleinsurance/consts";
import { CALCULATION_VEHICLE_INSURANCE_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_VEHICLE_INSURANCE } from "app/components/Sidebar/consts";

// Local Components
import VehicleInsuranceDetailCard from "./components/VehicleInsuranceDetailCard/VehicleInsuranceDetailCard";
import VehicleInsuranceDeleteCard from "./components/VehicleInsuranceDeleteCard/VehicleInsuranceDeleteCard";

const VehicleInsuranceDetailPage: VehicleInsuranceDetailPageCmp = (
  props: VehicleInsuranceDetailPageProps
) => {
  const { vehicleInsuranceId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] =
    useState<VehicleInsuranceDetailFormValues>(
      initialVehicleInsuranceDetailForm
    );

  const navigate = useNavigate();

  const title = CALCULATION_VEHICLE_INSURANCE_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (vehicleInsuranceId) fetchVehicleInsurance(vehicleInsuranceId);
  }, [vehicleInsuranceId]);

  const fetchVehicleInsurance = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialVehicleInsuranceDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getVehicleInsuranceAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_VEHICLE_INSURANCE.text,
      route: SIDEBAR_ITEM_CALCULATION_VEHICLE_INSURANCE.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.CalculationVehicleInsuranceSearchPageRouter, {
        vehicleInsuranceId,
      })
    );
  }, []);

  if (!vehicleInsuranceId) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white border-b border-slate-200 rounded-t-md">
          <PageHeader>
            <PageTitle
              title={`${title.text}`}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
              <div className="flex gap-2">
                <Button
                  size="large"
                  variant="remove"
                  children={t(
                    "admin.calculation.setting.vehicleinsurance.detail.delete"
                  )}
                  onClick={() => setShowDeleteModal(true)}
                  startIcon="/media/icons/trash.svg"
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-4 py-6  lg:px-4">
          <VehicleInsuranceDetailCard vehicleInsuranceId={vehicleInsuranceId} />
        </div>
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t("admin.calculation.setting.vehicleinsurance.delete.title")}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <VehicleInsuranceDeleteCard
              vehicleInsuranceId={vehicleInsuranceId}
              onSuccess={handleDeleteSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

VehicleInsuranceDetailPage.route =
  Routes.CalculationVehicleInsuranceDetailPageRouter;

export default VehicleInsuranceDetailPage;
